<script setup lang="ts">
import type { LoginMethod } from '@@/bits/auth_helper'
import device from '@@/bits/device'
import fetchElectronBridge, { URLTarget } from '@@/bits/electron_bridge'
import { __ } from '@@/bits/intl'
import { getSearchParams, navigateTo, transformUrl } from '@@/bits/location'
import { safeLocalStorage } from '@@/bits/safe_storage'
import OzDropdownButton, {
  OzDropdownButtonColorScheme,
  OzDropdownButtonSizePreset,
} from '@@/library/v4/components/OzDropdownButton.vue'
import { useAuthLoginStore } from '@@/pinia/auth_login_store'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import AuthLoginThirdPartyButton from '@@/vuecomponents/AuthLoginThirdPartyButton.vue'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const {
  loginMethods = {},
  type = AuthLoginThirdPartyGroupButtonsType.Continue,
  showMoreDropdown = true,
  shouldCachePreferredLoginMethod = false,
} = defineProps<{
  loginMethods?: Record<string, LoginMethod>
  type?: AuthLoginThirdPartyGroupButtonsType
  showMoreDropdown?: boolean
  shouldCachePreferredLoginMethod?: boolean
}>()

const { isDarkMode } = storeToRefs(useDarkModeStore())
const { isSchoolLibraryLoginPage } = storeToRefs(useAuthLoginStore())

const isApple = device.mac || device.ios

const appleText = computed<string>(() => {
  return {
    [AuthLoginThirdPartyGroupButtonsType.Continue]: __('Continue with Apple'),
    [AuthLoginThirdPartyGroupButtonsType.LogIn]: __('Log in with Apple'),
    [AuthLoginThirdPartyGroupButtonsType.SignUp]: __('Sign up with Apple'),
  }[type]
})

const googleText = computed<string>(() => {
  return {
    [AuthLoginThirdPartyGroupButtonsType.Continue]: __('Continue with Google'),
    [AuthLoginThirdPartyGroupButtonsType.LogIn]: __('Log in with Google'),
    [AuthLoginThirdPartyGroupButtonsType.SignUp]: __('Sign up with Google'),
  }[type]
})

const microsoftText = computed<string>(() => {
  return {
    [AuthLoginThirdPartyGroupButtonsType.Continue]: __('Continue with Microsoft'),
    [AuthLoginThirdPartyGroupButtonsType.LogIn]: __('Log in with Microsoft'),
    [AuthLoginThirdPartyGroupButtonsType.SignUp]: __('Sign up with Microsoft'),
  }[type]
})

const skoletubeText = computed<string>(() => {
  return {
    [AuthLoginThirdPartyGroupButtonsType.Continue]: __('Continue with Skoletube'),
    [AuthLoginThirdPartyGroupButtonsType.LogIn]: __('Log in with Skoletube'),
    [AuthLoginThirdPartyGroupButtonsType.SignUp]: __('Sign up with Skoletube'),
  }[type]
})

const classlinkText = computed<string>(() => {
  return {
    [AuthLoginThirdPartyGroupButtonsType.Continue]: __('Continue with ClassLink'),
    [AuthLoginThirdPartyGroupButtonsType.LogIn]: __('Log in with ClassLink'),
    [AuthLoginThirdPartyGroupButtonsType.SignUp]: __('Sign up with ClassLink'),
  }[type]
})

const cleverText = computed<string>(() => {
  return {
    [AuthLoginThirdPartyGroupButtonsType.Continue]: __('Continue with Clever'),
    [AuthLoginThirdPartyGroupButtonsType.LogIn]: __('Log in with Clever'),
    [AuthLoginThirdPartyGroupButtonsType.SignUp]: __('Sign up with Clever'),
  }[type]
})

const handleClick = (url: string | undefined, name: string): void => {
  if (!url) return
  const electronApp = fetchElectronBridge()
  const referralCode = getSearchParams().referral_code
  const oauthUrl = referralCode
    ? transformUrl(url, {
        searchParams: {
          referralCode,
        },
      })
    : url

  if (electronApp.openThirdPartyAuth) {
    return electronApp.openThirdPartyAuth(oauthUrl, URLTarget.Internal)
  }

  if (shouldCachePreferredLoginMethod) {
    safeLocalStorage.setItem('preferred_login_method', name)
  }

  navigateTo(oauthUrl)
}

const xAdditionalLoginOptionsList = ref(false)

const preferredLoginMethod = computed((): string | null => {
  return safeLocalStorage.getItem('preferred_login_method')
})

const baseOrder = isApple
  ? ['apple', 'google', 'microsoft', 'skoletube']
  : ['google', 'microsoft', 'apple', 'skoletube']
const additionalOrder = ['classlink', 'clever']

const orderedLoginMethods = computed(() => {
  const preferred = preferredLoginMethod.value
  if (!preferred) {
    return baseOrder
  }
  // If the most recently used method is hidden under the "more options" dropdown, append it as the fourth option with a badge.
  if (additionalOrder.includes(preferred)) {
    return [...baseOrder, preferred]
  }
  return baseOrder
})

const orderedAdditionalLoginMethods = computed(() => {
  const preferred = preferredLoginMethod.value
  if (preferred && additionalOrder.includes(preferred)) {
    return [...additionalOrder.filter((method) => method !== preferred)]
  }
  return additionalOrder
})

const filteredOrderedLoginMethods = computed(() => {
  return orderedLoginMethods.value.filter((methodName) => loginMethods[methodName])
})

const filteredAdditionalLoginMethods = computed(() => {
  return orderedAdditionalLoginMethods.value.filter((methodName) => loginMethods[methodName])
})

function isLastUsedMethod(methodName: string): boolean {
  return methodName === preferredLoginMethod.value
}

const areAdditionalLoginOptionsAvailable = computed<boolean>(
  () => loginMethods.skoletube != null || loginMethods.classlink != null || loginMethods.clever != null,
)

function getMethodIcon(methodName: string): string {
  if (methodName === 'clever') {
    return isDarkMode.value ? 'clever_blue' : 'clever_white'
  }
  if (methodName === 'microsoft') {
    return 'windows'
  }
  return methodName
}

function getMethodText(methodName: string): string {
  const textMap = {
    apple: appleText,
    google: googleText,
    microsoft: microsoftText,
    skoletube: skoletubeText,
    classlink: classlinkText,
    clever: cleverText,
  }
  return textMap[methodName].value || ''
}
</script>

<script lang="ts">
export enum AuthLoginThirdPartyGroupButtonsType {
  Continue = 'Continue',
  LogIn = 'Log in',
  SignUp = 'Sign up',
}
</script>

<template>
  <div class="flex flex-col gap-y-2 pb-2">
    <AuthLoginThirdPartyButton
      v-for="methodName in filteredOrderedLoginMethods"
      :key="methodName"
      :icon="getMethodIcon(methodName)"
      :text="getMethodText(methodName)"
      :data-testid="`${methodName}LoginButton`"
      :x-is-last-used-badge="shouldCachePreferredLoginMethod && isLastUsedMethod(methodName)"
      @click="handleClick(loginMethods[methodName].url, loginMethods[methodName].name)"
    />

    <div
      v-if="
        !isSchoolLibraryLoginPage &&
        !xAdditionalLoginOptionsList &&
        areAdditionalLoginOptionsAvailable &&
        showMoreDropdown
      "
      class="flex justify-end"
    >
      <OzDropdownButton
        class="px-3 gap-x-1"
        :text="__('More options')"
        :size-preset="OzDropdownButtonSizePreset.H32px"
        :color-scheme="OzDropdownButtonColorScheme.SecondaryClear"
        :text-color-classes="['text-dark-text-200 dark:text-light-text-200']"
        @click="xAdditionalLoginOptionsList = true"
      />
    </div>
    <template v-else>
      <AuthLoginThirdPartyButton
        v-for="methodName in filteredAdditionalLoginMethods"
        :key="methodName"
        :data-testid="`${methodName}LoginButton`"
        :icon="getMethodIcon(methodName)"
        :text="getMethodText(methodName)"
        @click="handleClick(loginMethods[methodName].url, loginMethods[methodName].name)"
      />
    </template>
  </div>
</template>
